import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import '../styles/components/team-detail.scss';
import { baseURL, baseURLnoapi, CURRENT_SEASON, AVAILABLE_SEASONS } from '../Constants';
import { getSeason1TeamById } from '../data/season1TeamPlayersData';

// Helper function to convert short team IDs (like "69" or "420") to UUID format for Season 1
const shortIdToUuid = (shortId: string): string => {
  // If already a UUID (contains hyphens), return as-is
  if (shortId.includes('-')) {
    return shortId;
  }
  // Convert short ID to UUID format: 00000000-0000-0000-0000-000000000069
  const paddedId = shortId.padStart(12, '0');
  return `00000000-0000-0000-0000-${paddedId}`;
};

// Define Player interface based on the API response (Season 2)
interface Player {
  steamID: string;
  name: string | null;
  dateOfBirth: string | null;
  playerName: string;
  twitter: string | null;
  role: string | null;
  team: any;
  isAdmin: boolean;
  passwordHash: string | null;
  discordId: string;
  discordUsername: string;
  age: number | null;
}

// Define Season 1 Player interface
interface Season1PlayerData {
  account: {
    steamID: string;
    name: string | null;
    age: number | null;
    playerName: string;
    lft: boolean;
    twitter: string | null;
    twitch: string | null;
    youtube: string | null;
    instagram: string | null;
    role: string | null;
    donator: boolean;
    premium: boolean;
    faceitElo: number | null;
    faceitNickname: string | null;
  };
  player: any | null;
  playerName: string | null;
  season: number;
  team: Season1Team;
  teamId: string | null;
  steamID: string;
  // Stats fields
  kills: number;
  hsKills: number;
  deaths: number;
  assists: number;
  hs: number;
  dmg: number;
  adr: number;
  kd: number;
  fk: number;
  kast: number;
  hltv1Rating: number;
  hltv2Rating: number;
  openingDuelWinPercentage: number;
  openingDuelAttemptPercentage: number;
  enemiesFlashed: number;
  utilityDamage: number;
  enemyFlashTime: number;
  timeFlashed: number;
  gamesPlayed: number;
  roundsPlayed: number;
  awpKills: number;
}

// Unified player interface for display
interface DisplayPlayer {
  steamID: string;
  name: string | null;
  playerName: string;
  role: string | null;
  age: number | null;
  season: number;
  // Stats (may not be available for all seasons)
  kills?: number;
  deaths?: number;
  kd?: number;
  hltv2Rating?: number;
  adr?: number;
  hs?: number;
}

// Define Team interface based on the C# model (Season 2)
interface Team {
  id: string;
  name: string;
  logo: string | null;
  seed?: string;
  division?: string;
  season?: number;
  currentDescription?: string | null;
  currentCoach?: Player | null;
  players: Player[];
}

// Define Season 1 Team interface
interface Season1Team {
  teamId: string;
  teamName: string;
  teamLogo: string;
  ownerId: string;
  season: number;
  division: string;
  twitter: string | null;
  coachID: string | null;
  website: string | null;
  seed: string;
  playoffSeed: string;
}

// Unified team interface for display
interface DisplayTeam {
  id: string;
  name: string;
  logo: string | null;
  seed?: string;
  division?: string;
  players: DisplayPlayer[];
  season: number;
  description?: string | null;
  currentCoach?: DisplayPlayer | null;
}

// Player Avatar Hook for reusable avatar loading logic
const usePlayerAvatar = (steamId: string, season: number) => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadAvatar = async () => {
      try {
        if (season === 1) {
          // For Season 1, use PowerStats avatar system
          // We'll set the primary URL and handle fallback in the img onError
          const primaryUrl = `https://api.powerstats.dk/PowerStats/api/Account/Profile/${steamId}`;
          setAvatarUrl(primaryUrl);
        } else {
          // For Season 2, use current avatar system
          const response = await fetch(`${baseURL}/Account/${steamId}/avatar`);
          if (response.ok) {
            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);
            setAvatarUrl(imageUrl);
          } else {
            // Try to import avatar if none exists
            await fetch(`${baseURL}/Account/${steamId}/import-avatar`);
            // Try fetching again after import
            const retryResponse = await fetch(`${baseURL}/Account/${steamId}/avatar`);
            if (retryResponse.ok) {
              const blob = await retryResponse.blob();
              const imageUrl = URL.createObjectURL(blob);
              setAvatarUrl(imageUrl);
            }
          }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadAvatar();

    // Cleanup function for blob URLs (only for Season 2)
    return () => {
      if (avatarUrl && season !== 1) {
        URL.revokeObjectURL(avatarUrl);
      }
    };
  }, [steamId, season]);

  return { avatarUrl, loading };
};

// Player content sub-component (extracted to module scope)
const PlayerContent: React.FC<{
  player: DisplayPlayer;
  teamSeason: number;
  avatarUrl: string | null;
  loading: boolean;
  formattedName: string;
}> = ({ player, teamSeason, avatarUrl, loading, formattedName }) => {
  const placeholderAvatar = 'https://via.placeholder.com/80x80/333/fff?text=Player';

  return (
    <>
      <div className="player-avatar">
        {loading ? (
          <div className="avatar-loading">
            <div className="loading-spinner small"></div>
          </div>
        ) : (
          <img
            src={avatarUrl || placeholderAvatar}
            alt={`${player.playerName}'s avatar`}
            className="avatar-img"
            onError={(e) => {
              const imgElement = e.target as HTMLImageElement;
              if (teamSeason === 1 && !imgElement.dataset.fallbackAttempted) {
                imgElement.dataset.fallbackAttempted = 'true';
                const fallbackUrl = `https://report.dpl.gg/powerfiles/ligaen/getPlayerPicture.php?id=${player.steamID}`;
                imgElement.src = fallbackUrl;
                imgElement.onerror = () => {
                  imgElement.src = placeholderAvatar;
                };
              } else {
                imgElement.src = placeholderAvatar;
              }
            }}
          />
        )}
      </div>

      <div className="player-info">
        <div className="player-name">{formattedName}</div>
        {player.role && (
          <div className="player-role">{player.role}</div>
        )}
      </div>

      {(player.kd !== undefined || player.hltv2Rating !== undefined) && (
        <div className="player-stats">
          {player.hltv2Rating !== undefined && (
            <div className="stat-item">
              <span className="stat-label">Rating</span>
              <span className="stat-value">{player.hltv2Rating.toFixed(2)}</span>
            </div>
          )}
          {player.kd !== undefined && (
            <div className="stat-item">
              <span className="stat-label">K/D</span>
              <span className="stat-value">{player.kd.toFixed(2)}</span>
            </div>
          )}
          {player.adr !== undefined && (
            <div className="stat-item">
              <span className="stat-label">ADR</span>
              <span className="stat-value">{player.adr.toFixed(1)}</span>
            </div>
          )}
          {player.hs !== undefined && (
            <div className="stat-item">
              <span className="stat-label">HS%</span>
              <span className="stat-value">{player.hs.toFixed(1)}%</span>
            </div>
          )}
        </div>
      )}
    </>
  );
};

// Player Card Component
const PlayerCard: React.FC<{ player: DisplayPlayer; teamSeason: number }> = ({ player, teamSeason }) => {
  const { avatarUrl, loading } = usePlayerAvatar(player.steamID, teamSeason);

  const getPlayerUrl = () => {
    if (teamSeason === 1) {
      return `https://rising.powerstats.dk/player/${player.steamID}`;
    } else {
      return `/profile/${player.steamID}`;
    }
  };

  const formatPlayerName = () => {
    const realName = player.name;
    const username = player.playerName;

    if (realName && realName.trim()) {
      const nameParts = realName.trim().split(' ');
      if (nameParts.length >= 2) {
        const firstName = nameParts[0];
        const lastName = nameParts[nameParts.length - 1];
        return `${firstName} "${username}" ${lastName}`;
      } else if (nameParts.length === 1) {
        return `${nameParts[0]} "${username}"`;
      }
    }
    return username;
  };

  const formattedName = formatPlayerName();

  return (
    <div className="player-card">
      {teamSeason === 1 ? (
        <a href={getPlayerUrl()} target="_blank" rel="noopener noreferrer" className="player-link">
          <PlayerContent player={player} teamSeason={teamSeason} avatarUrl={avatarUrl} loading={loading} formattedName={formattedName} />
        </a>
      ) : (
        <Link to={getPlayerUrl()} className="player-link">
          <PlayerContent player={player} teamSeason={teamSeason} avatarUrl={avatarUrl} loading={loading} formattedName={formattedName} />
        </Link>
      )}
    </div>
  );
};

const TeamDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [team, setTeam] = useState<DisplayTeam | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
  const [teamSeasons, setTeamSeasons] = useState<number[]>([]);

  // Get season from URL params (optional)
  const urlSeason = searchParams.get('season');
  const [currentSeason, setCurrentSeason] = useState<number | null>(() => {
    return urlSeason ? parseInt(urlSeason) : null;
  });
  
  const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

  useEffect(() => {
    const fetchTeam = async () => {
      setIsLoading(true);
      setError(null);

      // Normalize ID upfront for all API calls - converts short IDs like "69" or "420" to UUID format
      const normalizedId = id ? shortIdToUuid(id) : id;

      try {
        // First, check which seasons this team exists in
        const checkSeasons = async () => {
          const seasons: number[] = [];

          // Check current API for all seasons (2+)
          // Use normalized ID to avoid 400 errors from the backend
          try {
            const seasonsResponse = await fetch(`${baseURL}/Team/${normalizedId}/seasons`);
            if (seasonsResponse.ok) {
              const teamSeasons = await seasonsResponse.json();
              if (Array.isArray(teamSeasons)) {
                seasons.push(...teamSeasons);
              }
            }
          } catch (e) {
            console.error('Failed to fetch team seasons:', e);
          }

          // Also check Season 1 from PowerStats
          try {
            const season1Response = await fetch(`https://api.rising.powerstats.dk/RisingStats/api/Team/getTeamByTeamIdAndSeason?teamId=${normalizedId}&season=1`);
            if (season1Response.ok) {
              if (!seasons.includes(1)) {
                seasons.push(1);
              }
            }
          } catch (e) {}

          return seasons.sort((a, b) => b - a); // Sort descending
        };

        const detectedSeasons = await checkSeasons();
        setTeamSeasons(detectedSeasons);
        setAvailableSeasons(detectedSeasons);

        let teamData: DisplayTeam | null = null;
        let foundSeason: number | null = null;

        // Determine which season to fetch:
        // 1. Use URL param if specified
        // 2. Otherwise use the team's latest season (highest number)
        // 3. Fall back to CURRENT_SEASON if no seasons detected
        const seasonToFetch = currentSeason !== null
          ? currentSeason
          : (detectedSeasons.length > 0 ? Math.max(...detectedSeasons) : CURRENT_SEASON);

        // Update currentSeason if it wasn't set from URL
        if (currentSeason === null) {
          setCurrentSeason(seasonToFetch);
        }

        // Try to fetch team data for the specified season
        if (seasonToFetch >= 1) {
          try {
            if (seasonToFetch === 1) {
              // Use hardcoded Season 1 data (matches TeamsPage pattern)
              // Try both original ID and normalized UUID format for Season 1 special teams (69, 420)
              let season1Data = id ? getSeason1TeamById(id) : undefined;
              if (!season1Data && normalizedId) {
                // Try with normalized UUID format
                season1Data = getSeason1TeamById(normalizedId);
              }

              if (season1Data) {
                // Transform hardcoded data to DisplayPlayer format
                const players: DisplayPlayer[] = season1Data.players.map(player => ({
                  steamID: player.steamID,
                  name: player.name,
                  playerName: player.playerName,
                  role: player.role,
                  age: player.age,
                  season: 1,
                  // Stats not available in hardcoded data
                  kills: undefined,
                  deaths: undefined,
                  kd: undefined,
                  hltv2Rating: undefined,
                  adr: undefined,
                  hs: undefined
                }));

                // Fetch team logo from backend API (works for all seasons)
                // Always use normalizedId for API calls to ensure valid GUID format
                let logoUrl = null;
                try {
                  const teamLogoResponse = await fetch(`${baseURL}/Team/${normalizedId}?season=1`);
                  if (teamLogoResponse.ok) {
                    const teamLogoData = await teamLogoResponse.json();
                    logoUrl = teamLogoData.logo;
                  }
                } catch (error) {
                  console.log('Could not fetch logo from backend, will use fallback');
                }

                // Transform hardcoded data to DisplayTeam format
                teamData = {
                  id: season1Data.teamId,
                  name: season1Data.teamName,
                  logo: logoUrl,
                  seed: season1Data.seed,
                  division: season1Data.division,
                  players: players,
                  season: 1,
                  description: null, // Season 1 doesn't have custom descriptions
                  currentCoach: null // Season 1 coach system not implemented
                };
                foundSeason = 1;
              }
            } else {
              // Try Season 2+
              const season2Response = await fetch(`${baseURL}/Team/${normalizedId}?season=${seasonToFetch}`);

              if (season2Response.ok) {
                const season2TeamData: Team = await season2Response.json();

                // Fetch player stats from PowerStats API for Season 2+
                let players: DisplayPlayer[] = [];
                try {
                  const playersResponse = await fetch(`https://api.rising.powerstats.dk/RisingStats/api/PlayerStat/getPlayersByTeamId?teamid=${normalizedId}&season=${seasonToFetch}`);

                  if (playersResponse.ok) {
                    const season2PlayersData: Season1PlayerData[] = await playersResponse.json();

                    // Transform player data with stats
                    players = season2PlayersData.map(playerData => ({
                      steamID: playerData.account.steamID,
                      name: playerData.account.name,
                      playerName: playerData.account.playerName,
                      role: playerData.account.role,
                      age: playerData.account.age,
                      season: seasonToFetch,
                      kills: playerData.kills,
                      deaths: playerData.deaths,
                      kd: playerData.kd,
                      hltv2Rating: playerData.hltv2Rating,
                      adr: playerData.adr,
                      hs: playerData.hs
                    }));
                  }
                } catch (error) {
                  console.error(`Failed to fetch player stats for Season ${seasonToFetch}:`, error);
                  // Fallback to basic player data from team response
                  players = season2TeamData.players?.map(player => ({
                    steamID: player.steamID,
                    name: player.name,
                    playerName: player.playerName,
                    role: player.role,
                    age: player.age,
                    season: season2TeamData.season || 2
                  })) || [];
                }

                teamData = {
                  id: season2TeamData.id,
                  name: season2TeamData.name,
                  logo: season2TeamData.logo,
                  seed: season2TeamData.seed,
                  division: season2TeamData.division,
                  players: players,
                  season: season2TeamData.season || seasonToFetch,
                  description: season2TeamData.currentDescription,
                  currentCoach: season2TeamData.currentCoach ? {
                    steamID: season2TeamData.currentCoach.steamID,
                    name: season2TeamData.currentCoach.name,
                    playerName: season2TeamData.currentCoach.playerName,
                    role: season2TeamData.currentCoach.role,
                    age: season2TeamData.currentCoach.age,
                    season: season2TeamData.season || seasonToFetch
                  } : null
                };
                foundSeason = seasonToFetch;
              }
            }
          } catch (seasonError) {
            console.error(`Error fetching team for season ${seasonToFetch}:`, seasonError);
          }
        }

        if (!teamData) {
          throw new Error(`Team not found for season ${seasonToFetch}`);
        }

        setTeam(teamData);
      } catch (error) {
        console.error('Error fetching team:', error);
        setError('Failed to load team data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (id) {
      fetchTeam();
    }
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Set page title - will be updated when team loads
  }, [id, currentSeason]);

  // Update page title when team data loads
  useEffect(() => {
    if (team) {
      document.title = `${team.name} - Season ${team.season} | Northern Championships Series`;
    } else {
      document.title = 'Team Details | Northern Championships Series';
    }
  }, [team]);

  // Handle season change
  const handleSeasonChange = (newSeason: number) => {
    setCurrentSeason(newSeason);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('season', newSeason.toString());
    setSearchParams(newSearchParams);
  };

  // Update currentSeason when URL changes
  useEffect(() => {
    const urlSeason = searchParams.get('season');
    if (urlSeason) {
      const season = parseInt(urlSeason);
      if (season !== currentSeason) {
        setCurrentSeason(season);
      }
    } else if (currentSeason !== null && !urlSeason) {
      // If URL param is removed, reset to null to trigger auto-detection
      setCurrentSeason(null);
    }
  }, [searchParams.get('season')]);

  // Helper function to get team logo URL based on season
  const getTeamLogoUrl = () => {
    if (!team) return teamPlaceholderImage;
    
    if (team.season === 1) {
      // Season 1: Use direct URLs from PowerStats API
      return team.logo || teamPlaceholderImage;
    } else {
      // Season 2: Use current logic
      if (team.logo && team.logo.startsWith('http')) {
        return team.logo;
      } else if (team.logo) {
        return `${baseURLnoapi}${team.logo}`;
      } else {
        // Get logo from the team logo endpoint
        return `${baseURL}/Team/${team.id}/logo`;
      }
    }
  };

  // If still loading, show loading spinner
  if (isLoading) {
    return (
      <div className="team-detail-page">
        <div className="container">
          <div className="loading">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  // If error or no team, show error message
  if (error || !team) {
    return (
      <div className="team-detail-page">
        <div className="container">
          <div className="error-container">
            <h2>Team Not Found</h2>
            <p>{error || "We couldn't find the team you're looking for."}</p>
            <Link to="/teams" className="btn btn-primary">View All Teams</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="team-detail-page">
      <div className="team-header-section">
        <div className="container-wide">
          <div className="team-header-card">
            {/* Mobile header: logo centered, team name below, coach in corner */}
            <div className="mobile-top-row">
              {team.currentCoach && (
                <div className="mobile-coach">
                  <span className="coach-label">Coach</span>
                  <div className="coach-avatar">
                    {team.season === 1 ? (
                      <a href={`https://stats.rising.powerstats.dk/player/${team.currentCoach.steamID}`} target="_blank" rel="noopener noreferrer">
                        <img src={`https://api.powerstats.dk/PowerStats/api/Account/Profile/${team.currentCoach.steamID}`} alt={team.currentCoach.playerName} />
                      </a>
                    ) : (
                      <Link to={`/profile/${team.currentCoach.steamID}`}>
                        <img src={`${baseURL}/Account/${team.currentCoach.steamID}/avatar`} alt={team.currentCoach.playerName} />
                      </Link>
                    )}
                  </div>
                  <span className="coach-name">{team.currentCoach.playerName}</span>
                </div>
              )}
              <div className="team-logo">
                <img
                  src={getTeamLogoUrl()}
                  alt={`${team.name} logo`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = teamPlaceholderImage;
                  }}
                />
              </div>
              <h1 className="mobile-team-name">{team.name}</h1>
              {team.division && (
                <div className="mobile-team-meta">
                  <span className="meta-label">Group:</span>
                  <span className="meta-value">{team.division}</span>
                </div>
              )}
            </div>

            <div className="team-header-left">
              <div className="team-branding">
                <div className="team-logo">
                  <img
                    src={getTeamLogoUrl()}
                    alt={`${team.name} logo`}
                    onError={(e) => {
                      // Fallback to placeholder if logo loading fails
                      (e.target as HTMLImageElement).src = teamPlaceholderImage;
                    }}
                  />
                </div>
                <h1>{team.name}</h1>
                {team.division && (
                  <div className="team-meta-compact">
                    <div className="meta-item">
                      <span className="meta-label">Group:</span>
                      <span className="meta-value">{team.division}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="team-header-center">
              <div className="team-about">
                <h3>About</h3>
                <p>
                  {team.description ? (
                    team.description
                  ) : (
                    <>
                      {team.name} is a competitive esports team participating in the Northern Championships Series.
                      {team.division && ` Currently competing in Group ${team.division}`}
                      {team.seed && ` with a seed of #${team.seed}`}.
                    </>
                  )}
                </p>
              </div>
            </div>

            {team.currentCoach && (
              <div className="team-header-right">
                <div className="coach-section">
                  <h3>Coach</h3>
                  <div className="coach-card-compact">
                    {team.season === 1 ? (
                      <a href={`https://stats.rising.powerstats.dk/player/${team.currentCoach.steamID}`} target="_blank" rel="noopener noreferrer" className="coach-link">
                        <div className="coach-avatar-square">
                          <img src={`https://api.powerstats.dk/PowerStats/api/Account/Profile/${team.currentCoach.steamID}`} alt={team.currentCoach.playerName} />
                        </div>
                        <div className="coach-info">
                          <div className="coach-name">{team.currentCoach.playerName}</div>
                          {team.currentCoach.name && <div className="coach-real-name">{team.currentCoach.name}</div>}
                        </div>
                      </a>
                    ) : (
                      <Link to={`/profile/${team.currentCoach.steamID}`} className="coach-link">
                        <div className="coach-avatar-square">
                          <img src={`${baseURL}/Account/${team.currentCoach.steamID}/avatar`} alt={team.currentCoach.playerName} />
                        </div>
                        <div className="coach-info">
                          <div className="coach-name">{team.currentCoach.playerName}</div>
                          {team.currentCoach.name && <div className="coach-real-name">{team.currentCoach.name}</div>}
                        </div>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="team-content">
        <div className="container-wide">
          {/* Hide roster section for TBD placeholder team */}
          {team.name !== "TBD" && (
            <section className="team-roster">
              <div className="roster-header">
                <h2>Roster - Season {team.season}</h2>
                <select
                  value={currentSeason ?? CURRENT_SEASON}
                  onChange={(e) => handleSeasonChange(parseInt(e.target.value))}
                  className="season-select-roster"
                >
                  {teamSeasons.length > 0 ? (
                    teamSeasons.map(s => (
                      <option key={s} value={s}>Season {s}</option>
                    ))
                  ) : (
                    <option value={currentSeason ?? CURRENT_SEASON}>
                      Season {currentSeason ?? CURRENT_SEASON}
                    </option>
                  )}
                </select>
              </div>
              {team.players && team.players.length > 0 ? (
                <div className="player-grid">
                  {team.players.map((player) => (
                    <PlayerCard key={player.steamID} player={player} teamSeason={team.season} />
                  ))}
                </div>
              ) : (
                <div className="empty-roster">
                  <p>No players have been added to this team yet.</p>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamDetailPage;